<section class="workforce" aria-label="Workforce snapshot">
  @if (state.mode === 'resolving') {
    <p role="status">Loading workforce context…</p>
  }
  @if (state.error) {
    <p role="alert">{{ state.error }}</p>
  }
  @if (selectionOnly && state.mode === 'legacy') {
    <p role="alert">Workforce access changed. Close this dialog and reopen Teams.</p>
  }
  @if (state.mode === 'blocked') {
    <button mat-stroked-button type="button" (click)="state.open(state.companyId)">Retry workforce access</button>
  }
  @if (state.notice) {
    <p role="status">{{ state.notice }}</p>
  }
  @if (state.mode === 'snapshot') {
    <header>
      <div>
        <h2>{{ selectionOnly ? 'Select members' : 'All Employees' }}</h2>
        @if (state.context?.snapshot) {
          <p>Snapshot built {{ $safeNavigationMigration(state.context?.snapshot?.builtAt) | date:'medium' }} · {{ state.age }}</p>
        }
      </div>
      <button mat-stroked-button type="button" (click)="state.reload()" [disabled]="creating || state.loading">Reload snapshot</button>
    </header>
    @if (!selectionOnly && state.context?.synchronization; as sync) {
      <p class="sync" role="status">
        Synchronization: {{ sync.status }}@if (sync.stage) {
        <span> · {{ sync.stage }}</span>
      }
      @if (sync.lastAttemptOutcome && sync.lastAttemptOutcome !== 'none') {
        <span> · Latest attempt: {{ sync.lastAttemptOutcome }}</span>
      }
      @if (sync.completedAt) {
        <span> · {{ sync.completedAt | date:'medium' }}</span>
      }
      @if (sync.safeFailureCode) {
        <span> · {{ sync.safeFailureCode.split('_').join(' ') }}</span>
      }
    </p>
  }
  @for (warning of state.warnings; track warning) {
    <p class="warning">{{ warning }}</p>
  }
  @if (state.context?.availability === 'no_promoted_snapshot') {
    <p>Waiting for the first verified workforce snapshot.</p>
  }
  @if (state.context?.availability === 'provider_disconnected') {
    <p>The payroll connection is disconnected. Snapshot browsing is unavailable.</p>
  }
  @if (state.context?.availability === 'snapshot_unavailable') {
    <p>The promoted snapshot is unavailable for browsing.</p>
  }
  @if (state.context?.availability === 'ready') {
    @if (!selectionOnly) {
      <app-payroll-workforce-criteria [companyId]="state.companyId" [snapshotId]="state.context?.snapshot?.id || ''" [creation]="creation" (selector)="matchingChange.emit($event)" (selectionMode)="matchingMode = $event; state.selection.clear()" (criteriaChanged)="state.selection.clear()"></app-payroll-workforce-criteria>
    }
    @if (!memberEditing) {
      <div class="selection-bar" aria-live="polite">
        <div><strong>{{ matchingMode ? (matching ? matching.eligibleRows + ' eligible matches' : 'Preview criteria to select employees') : state.selection.size + ' selected across pages' }}</strong>
        <p>{{ matchingMode ? 'Criteria are saved with the new team.' : 'Choose individual employees, including across pages.' }}</p>
      </div>
      @if (!selectionOnly) {
        <button mat-raised-button color="primary" type="button" (click)="createTeam.emit()"
        [disabled]="(matchingMode ? !matching || !matching.eligibleRows : !state.selection.size) || creating">Create team</button>
      }
      @if (!matchingMode && state.selection.size) {
        <button mat-button type="button" [disabled]="creating" (click)="state.selection.clear()">Clear selection</button>
      }
    </div>
  }
  @if (!matchingMode && state.selection.size) {
    <details class="selected-employees">
      <summary>Review {{ state.selection.size }} selected employees</summary>
      <mat-chip-set aria-label="Selected employees">
        @for (entry of state.selection | keyvalue; track entry) {
          <mat-chip [removable]="!creating" (removed)="state.selection.delete(entry.key)">
            {{ entry.value.name || 'Employee' }}
            @if (!creating) {
              <button matChipRemove [attr.aria-label]="'Deselect ' + (entry.value.name || 'employee')"><mat-icon>cancel</mat-icon></button>
            }
          </mat-chip>
        }
      </mat-chip-set>
    </details>
  }
  <p class="muted">Up to 100 employee entries may be prepared in one operation. Search and browse filters only change the displayed employees; they do not become saved membership criteria.</p>
  <form class="controls search-controls" (ngSubmit)="apply()">
    <mat-form-field appearance="outline"><mat-label>Search by</mat-label><mat-select [disabled]="creating" name="searchField" [(ngModel)]="state.query.searchField" (selectionChange)="apply()"><mat-option value="lastName">Last name</mat-option><mat-option value="primaryEmail">Primary email</mat-option></mat-select></mat-form-field>
    <mat-form-field appearance="outline" class="search-input"><mat-label>Starts with</mat-label><input matInput [disabled]="creating" name="search" [(ngModel)]="state.query.search" maxlength="191" /></mat-form-field>
    <button mat-stroked-button type="submit" [disabled]="creating">Search</button>
    <mat-form-field appearance="outline"><mat-label>Sort by</mat-label><mat-select [disabled]="creating" name="sort" [(ngModel)]="state.query.sort" (selectionChange)="apply()"><mat-option value="name">Name</mat-option><mat-option value="email">Email</mat-option></mat-select></mat-form-field>
    <mat-form-field appearance="outline"><mat-label>Order</mat-label><mat-select [disabled]="creating" name="order" [(ngModel)]="state.query.order" (selectionChange)="apply()"><mat-option value="asc">Ascending</mat-option><mat-option value="desc">Descending</mat-option></mat-select></mat-form-field>
  </form>
  <div class="controls">
    <mat-form-field appearance="outline"><mat-label>Browse filter</mat-label><mat-select [disabled]="creating" [(ngModel)]="optionField" (selectionChange)="optionFieldChanged()">@for (field of state.filterFields; track field) {
    <mat-option [value]="field">{{ labels[field] }}</mat-option>
  }</mat-select></mat-form-field>
  <mat-form-field appearance="outline"><mat-label>Value starts with</mat-label><input matInput [disabled]="creating" [(ngModel)]="optionPrefix" (ngModelChange)="options()" maxlength="191" /></mat-form-field>
  <button mat-stroked-button type="button" (click)="options()" [disabled]="creating || state.optionsLoading">Find values</button>
  @if (state.options.length) {
    <mat-form-field appearance="outline"><mat-label>Choose exact value</mat-label><mat-select [value]="null" [disabled]="creating || state.optionsLoading" (selectionChange)="choose($event.value)">@for (value of state.options; track value) {
    <mat-option [value]="value">{{ value || '(empty value)' }}</mat-option>
  }</mat-select></mat-form-field>
}
@if (state.optionsNext) {
  <button mat-stroked-button type="button" (click)="options(true)" [disabled]="creating || state.optionsLoading">More values</button>
}
@if (state.optionsLoading) {
  <span role="status">Finding values…</span>
}
</div>
<mat-chip-set aria-label="Active browse filters">
  @for (field of state.filterFields; track field) {
    @if (state.query[field] !== undefined) {
      <mat-chip [removable]="!creating" (removed)="clear(field)">{{ labels[field] }}: {{ state.query[field] || '(empty value)' }}@if (!creating) {
        <button matChipRemove [attr.aria-label]="'Remove ' + labels[field] + ' filter'"><mat-icon>cancel</mat-icon></button>
      }</mat-chip>
    }
  }
</mat-chip-set>
@if (state.loading) {
  <mat-progress-bar mode="indeterminate" aria-label="Loading employees"></mat-progress-bar>
}
@if (state.loading) {
  <p role="status">Loading employees…</p>
}
<div class="page-summary">@if (state.page) {
  <p role="status">{{ state.rows.length }} shown · {{ state.page.counts?.filtered }} matching · {{ state.context?.counts?.browsable }} browsable in snapshot</p>
  }@if (!matchingMode) {
  <button mat-button type="button" (click)="state.selectPage()" [disabled]="creating || !state.canSelectPage">Select eligible on this page</button>
}</div>
@if (!state.loading && state.page && !state.rows.length) {
  <p>{{ state.context?.counts?.browsable === '0' ? 'This verified snapshot has no browsable employees.' : 'No employees match this search and filters.' }}</p>
}
<div class="employee-grid" role="list" aria-label="Employees" [attr.aria-busy]="state.loading">
  <div class="employee-headings" aria-hidden="true"><span></span><span>Employee</span><span>Department / job title</span><span>Details</span></div>
  @for (row of state.rows; track trackRow($index, row)) {
    <article role="listitem">
      <mat-checkbox [checked]="state.isSelected(row)" (change)="state.toggleSelection(row)"
        [disabled]="matchingMode || creating || !state.canSelect(row) || (!row.membership?.memberId && !state.selection.has(row.employeeKey) && state.selection.size >= 100)"
      [aria-label]="'Select ' + (row.firstName || '') + ' ' + (row.lastName || '')"></mat-checkbox>
      <div><strong>{{ row.lastName }}, {{ row.firstName }}</strong><p>{{ row.primaryEmail || 'No primary email' }}</p>@if (row.membership?.memberId) {
      <span class="muted">Current member</span>
      }@if (!state.canSelect(row)) {
      <span class="muted">{{ row.membership?.reason ? membershipReason($safeNavigationMigration(row.membership?.reason)) : 'Not eligible for selection' }}</span>
    }</div>
    <div>{{ row.department || 'No department' }}<p>{{ row.jobTitle || 'No job title' }}</p></div>
    <details><summary>View details</summary><dl>
    <dt>Employee number</dt><dd>{{ row.employeeNumber || '—' }}</dd>
    <dt>Employment status</dt><dd>{{ row.employmentStatus || '—' }}</dd>
    <dt>Active eligibility</dt><dd>{{ row.activeEligibility || '—' }}</dd>
    <dt>Payroll participation</dt><dd>{{ row.payrollParticipationStatus || '—' }}</dd>
    <dt>Location</dt><dd>{{ row.location || '—' }}</dd>
    <dt>Division</dt><dd>{{ row.division || '—' }}</dd>
  </dl></details>
</article>
}
</div>
@if (!state.loading && state.page && !state.rows.length) {
  <button mat-button type="button" (click)="clearFilters()" [disabled]="creating">Clear search and browse filters</button>
}
<nav aria-label="Employee pages"><button mat-stroked-button type="button" (click)="state.previous()" [disabled]="creating || state.loading || !state.history.length">Previous</button><button mat-stroked-button type="button" (click)="state.next()" [disabled]="creating || state.loading || !state.page?.nextCursor">Next</button></nav>
}
}
</section>
